import { useMemo } from 'react';

import { SystemRoleName } from '@jebel/constants';

import { RoleAddonInfoFragment } from 'shared/graphql';
import { RoleAddonPermission, RoleAddonPermissions } from 'shared/types';

import { useCurrentUserRoleAddons } from './useCurrentUserRoleAddons';
import { useCurrentUserRoles } from './useCurrentUserRoles';

const DEFAULT_PERMISSION: RoleAddonPermission = {
  add: false,
  edit: false,
  delete: false,
};

const PLATFORM_ADMIN_PERMISSION: RoleAddonPermission = {
  add: true,
  edit: true,
  delete: true,
};

const DEFAULT_PERMISSIONS: RoleAddonPermissions = {
  homePostsPermissions: DEFAULT_PERMISSION,
  schoolPostsPermissions: DEFAULT_PERMISSION,
  membersPermissions: DEFAULT_PERMISSION,
  businessPermissions: DEFAULT_PERMISSION,
  forumPermissions: DEFAULT_PERMISSION,
  groupsPermissions: DEFAULT_PERMISSION,
};

const PLATFORM_ADMIN_PERMISSIONS: RoleAddonPermissions = {
  homePostsPermissions: PLATFORM_ADMIN_PERMISSION,
  schoolPostsPermissions: PLATFORM_ADMIN_PERMISSION,
  membersPermissions: PLATFORM_ADMIN_PERMISSION,
  businessPermissions: PLATFORM_ADMIN_PERMISSION,
  forumPermissions: PLATFORM_ADMIN_PERMISSION,
  groupsPermissions: PLATFORM_ADMIN_PERMISSION,
};

function calculatePermissions(roles: RoleAddonInfoFragment[]): RoleAddonPermissions {
  const permissions: RoleAddonPermissions = { ...DEFAULT_PERMISSIONS };

  for (const role of roles) {
    const keys = Object.keys(DEFAULT_PERMISSIONS);

    for (const key of keys) {
      const newest = role[key];
      const curr = permissions[key];

      permissions[key] = {
        add: newest?.add || curr.add,
        edit: newest?.edit || curr.edit,
        delete: newest?.delete || curr.delete,
      };
    }
  }

  return permissions;
}

/**
 * Custom hook that retrieves the current user's permissions.
 * @returns An object containing the current user's permissions.
 */
export function useCurrentUserPermissions() {
  const { isPlatformAdmin, loading: systemRolesLoading } = useCurrentUserRoles();
  const { data: roleAddons, loading: roleAddonsLoading } = useCurrentUserRoleAddons();

  const permissions: RoleAddonPermissions = useMemo(() => {
    if (isPlatformAdmin) {
      return PLATFORM_ADMIN_PERMISSIONS;
    }

    return calculatePermissions(roleAddons);
  }, [roleAddons, isPlatformAdmin]);

  const isCommunityAdministrator = useMemo(() => {
    const hasCommunityAdminRole = roleAddons.some(
      role => role.role?.name === SystemRoleName.CommunityAdmin,
    );

    return isPlatformAdmin || hasCommunityAdminRole;
  }, [roleAddons, isPlatformAdmin]);

  return {
    /**
     * Current user permissions normalized.
     */
    permissions,

    /**
     * Boolean value indicating whether the current user is an admin or not.
     * @deprecated Use `isCommunityAdministrator` instead.
     */
    isSchoolAdmin: isCommunityAdministrator,

    /**
     * Whether the current user is a community administrator of the selected community.
     * This is `true` if the user is a platform admin or has a community admin role.
     */
    isCommunityAdministrator,

    loading: roleAddonsLoading || systemRolesLoading,
  };
}

/**
 * A function that takes the current user's permissions and returns a boolean value indicating whether the permission is selected or not.
 */
export type PermissionSelector = (permissions: RoleAddonPermissions) => boolean;

/**
 * A custom hook that selects a specific permission from the current user's permissions.
 * @param selector - A function that takes the current user's permissions and returns a boolean value indicating whether the permission is selected or not.
 * @returns A memoized boolean value indicating whether the selected permission is granted or not.
 */
export function useCurrentUserPermissionSelector(selector: PermissionSelector): boolean {
  const { permissions } = useCurrentUserPermissions();

  return useMemo(() => selector(permissions), [selector]);
}

/**
 * Custom hook to determine if the current user has permission to add home posts.
 * @returns {boolean} - Returns `true` if the user has the permission to add home posts, otherwise `false`.
 */
export function useCanCreateHomePost(): boolean {
  return useCurrentUserPermissionSelector(p => p.homePostsPermissions.add);
}

/**
 * Custom hook to determine if the current user has permission to request a group.
 * @returns {boolean} - Returns `true` if the user has the permission to add groups, otherwise `false`.
 */
export function useCanRequestGroup(): boolean {
  return useCurrentUserPermissionSelector(p => p.groupsPermissions.add);
}

/**
 * Custom hook to determine if the current user has permission to create businesses.
 * @returns {boolean} - A boolean indicating whether the current user has the permission to add businesses.
 */
export function useCanCreateBusiness(): boolean {
  return useCurrentUserPermissionSelector(p => p.businessPermissions.add);
}

/**
 * Custom hook to determine if the current user has permission to invite/add other member's membership.
 * @returns {boolean} - A boolean indicating whether the current user has the permission to invite/add other member's membership.
 */
export function useCanAddMembers(): boolean {
  return useCurrentUserPermissionSelector(p => p.membersPermissions.add);
}

/**
 * Custom hook to determine if the current user has permission to edit other member's membership.
 * @returns {boolean} - A boolean indicating whether the current user has the permission to edit other member's membership.
 */
export function useCanEditMembers(): boolean {
  return useCurrentUserPermissionSelector(p => p.membersPermissions.edit);
}

/**
 * Custom hook to determine if the current user has permission to revoke/reject/remove other member's membership.
 * @returns {boolean} - A boolean indicating whether the current user has the permission to revoke/reject/remove other member's membership.
 */
export function useCanDeleteMembers(): boolean {
  return useCurrentUserPermissionSelector(p => p.membersPermissions.delete);
}
